import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { updateVehiculeKms } from "../../store/actions/entretienActions"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import {
	STATUTS,
	Pill,
	echeanceDetail,
	echeanceTitre,
	fmtDate,
	fmtKm,
	ficheTone,
	tableStyles as ts,
} from "./entretienUtils"

const TYPES = [
	["tous", "Tous"],
	["cycliques", "Cycliques"],
	["uniques", "Uniques"],
]
const NB_COLS = 9

const styles = {
	bar: {
		display: "flex",
		gap: 8,
		flexWrap: "wrap",
		alignItems: "center",
		marginBottom: 12,
	},
	search: { ...gs.input, flex: "1 1 220px", minWidth: 0 },
	select: { ...gs.select, flex: "0 1 220px", minWidth: 0 },
	sev: (color) => ({
		display: "block",
		width: 4,
		height: 44,
		borderRadius: "0 3px 3px 0",
		backgroundColor: color,
	}),
	grpTd: {
		...ts.td,
		backgroundColor: COLORS.bg,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		padding: "8px 14px",
	},
	grpName: {
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 700,
		marginRight: 8,
	},
	kmInput: {
		...gs.input,
		flex: "none",
		width: 100,
		padding: "3px 8px",
		marginLeft: 8,
	},
	more: (hover) => ({
		border: "none",
		background: hover ? COLORS.bg : "none",
		color: hover ? COLORS.text : COLORS.textMuted,
		cursor: "pointer",
		fontSize: 18,
		lineHeight: 1,
		padding: "2px 8px",
		borderRadius: 6,
		fontFamily: "inherit",
	}),
	menu: {
		position: "absolute",
		right: 0,
		top: "100%",
		marginTop: 4,
		zIndex: 20,
		minWidth: 180,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		boxShadow: "0 6px 20px rgba(28, 25, 23, 0.10)",
		padding: 4,
	},
	menuItem: (hover, danger) => ({
		display: "block",
		width: "100%",
		textAlign: "left",
		padding: "8px 12px",
		border: "none",
		borderRadius: 7,
		backgroundColor: hover ? COLORS.bg : "transparent",
		color: danger ? COLORS.danger : COLORS.text,
		fontSize: 13,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
}

// Menu ⋯ d'une ligne : survol en state, fermeture au clic extérieur.
export const RowMenu = ({ items }) => {
	const [open, setOpen] = useState(false)
	const [hoverBtn, setHoverBtn] = useState(false)
	const [hoverItem, setHoverItem] = useState(null)
	const ref = useRef(null)
	useEffect(() => {
		if (!open) return
		const close = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false)
		}
		document.addEventListener("mousedown", close)
		return () => document.removeEventListener("mousedown", close)
	}, [open])
	return (
		<div
			ref={ref}
			style={{ position: "relative", display: "inline-block" }}
			onClick={(e) => e.stopPropagation()}
		>
			<button
				type="button"
				aria-label="Actions"
				style={styles.more(hoverBtn || open)}
				onMouseEnter={() => setHoverBtn(true)}
				onMouseLeave={() => setHoverBtn(false)}
				onClick={() => setOpen((o) => !o)}
			>
				⋯
			</button>
			{open && (
				<div style={styles.menu}>
					{items.map((it, i) => (
						<button
							key={it.label}
							type="button"
							style={styles.menuItem(hoverItem === i, it.danger)}
							onMouseEnter={() => setHoverItem(i)}
							onMouseLeave={() => setHoverItem(null)}
							onClick={() => {
								setOpen(false)
								it.onClick()
							}}
						>
							{it.label}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

// En-tête de groupe « par véhicule » avec km modifiable (impacte les échéances km).
const GroupeVehicule = ({ vehicule }) => {
	const dispatch = useDispatch()
	const [editing, setEditing] = useState(false)
	const [value, setValue] = useState(String(vehicule.kms))
	const save = () => {
		setEditing(false)
		// Séparateurs de milliers tolérés (« 199'585 ») : parseInt s'arrêtait
		// à l'apostrophe et enregistrait 199 km. Autre saisie : rien ne change.
		const brut = value.replace(/['’\s]/g, "")
		const kms = /^\d+$/.test(brut) ? parseInt(brut, 10) : NaN
		if (!isNaN(kms) && kms !== vehicule.kms)
			dispatch(updateVehiculeKms({ id: vehicule.id, kms }))
	}
	return (
		<tr style={ts.tr}>
			<td colSpan={NB_COLS} style={styles.grpTd}>
				<span style={styles.grpName}>{vehicule.label}</span>
				{vehicule.sous_label && `${vehicule.sous_label} · `}
				{editing ? (
					<input
						type="text"
						autoFocus
						style={styles.kmInput}
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onBlur={save}
						onKeyUp={(e) => e.key === "Enter" && save()}
					/>
				) : (
					<>
						{fmtKm(vehicule.kms)}
						<button
							type="button"
							style={{ ...ts.link, marginLeft: 8 }}
							onClick={() => {
								setValue(String(vehicule.kms))
								setEditing(true)
							}}
						>
							Modifier le km
						</button>
					</>
				)}
			</td>
		</tr>
	)
}

const EcheanceRow = ({
	e,
	hovered,
	setHovered,
	onOpen,
	onCreerFiche,
	onVoirFiche,
	menuItems,
}) => {
	const st = STATUTS[e.statut] || STATUTS.a_venir
	const td = ts.td
	return (
		<tr
			tabIndex={0}
			style={{
				cursor: "pointer",
				backgroundColor: hovered ? "#FAF9F7" : "transparent",
			}}
			onMouseEnter={() => setHovered(e.cycle_id)}
			onMouseLeave={() => setHovered(null)}
			onClick={() => onOpen(e)}
			onKeyDown={(ev) => ev.key === "Enter" && onOpen(e)}
		>
			<td style={{ ...td, width: 4, padding: 0 }}>
				<span style={styles.sev(st.color)} />
			</td>
			<td style={td}>
				<Pill tone={st}>{st.label}</Pill>
			</td>
			<td style={td}>
				<div style={ts.main}>{e.vehicule.label}</div>
				<div style={ts.sub}>
					{[e.vehicule.sous_label, fmtKm(e.vehicule.kms)]
						.filter(Boolean)
						.join(" · ")}
				</div>
			</td>
			<td style={td}>
				<div style={ts.main}>
					{e.entretien.label}
					{e.entretien.unique && <span style={ts.tag}>Unique</span>}
				</div>
				<div style={ts.sub}>
					{e.entretien.partie_label
						? `Partie : ${e.entretien.partie_label}`
						: e.entretien.portee_label}
				</div>
			</td>
			<td style={td}>{e.entretien.cycle_label}</td>
			<td style={td}>
				{e.dernier ? (
					fmtDate(e.dernier.date) || "—"
				) : (
					<span style={ts.sub}>—</span>
				)}
			</td>
			<td style={td}>
				<div
					style={{
						fontWeight: 700,
						color: e.statut === "a_venir" ? COLORS.text : st.color,
					}}
				>
					{echeanceTitre(e)}
				</div>
				<div style={ts.sub}>{echeanceDetail(e)}</div>
			</td>
			<td style={td}>
				{e.fiche ? (
					<button
						type="button"
						style={{ ...ts.link, textAlign: "left" }}
						title="Ouvrir la fiche"
						onClick={(ev) => {
							ev.stopPropagation()
							onVoirFiche(e.fiche.id)
						}}
					>
						<Pill tone={ficheTone(e.fiche.etat)}>
							{e.fiche.etat_label}
						</Pill>
						<div style={ts.sub}>Fiche n° {e.fiche.id}</div>
					</button>
				) : (
					<button
						type="button"
						style={ts.smallBtn}
						onClick={(ev) => {
							ev.stopPropagation()
							onCreerFiche(e)
						}}
					>
						Créer la fiche
					</button>
				)}
			</td>
			<td style={{ ...td, textAlign: "right" }}>
				<RowMenu items={menuItems(e)} />
			</td>
		</tr>
	)
}

const EntretienEcheances = ({
	echeances,
	statutFiltre,
	onOpen,
	onCreerFiche,
	onVoirFiche,
	menuItems,
}) => {
	const [q, setQ] = useState("")
	const [vehiculeId, setVehiculeId] = useState("")
	const [type, setType] = useState("tous")
	const [grouper, setGrouper] = useState(false)
	const [hovered, setHovered] = useState(null)

	const vehicules = useMemo(() => {
		const byId = {}
		echeances.forEach((e) => (byId[e.vehicule.id] = e.vehicule))
		return Object.values(byId).sort((a, b) =>
			a.label.localeCompare(b.label),
		)
	}, [echeances])

	const liste = useMemo(() => {
		const needle = q.trim().toLowerCase()
		return echeances.filter((e) => {
			if (statutFiltre && e.statut !== statutFiltre) return false
			if (vehiculeId && e.vehicule.id !== parseInt(vehiculeId))
				return false
			if (type === "cycliques" && e.entretien.unique) return false
			if (type === "uniques" && !e.entretien.unique) return false
			if (
				needle &&
				!`${e.vehicule.label} ${e.vehicule.sous_label} ${e.entretien.label}`
					.toLowerCase()
					.includes(needle)
			)
				return false
			return true
		})
	}, [echeances, statutFiltre, vehiculeId, type, q])

	// Groupage : ordre des véhicules = ordre d'urgence de leur 1re ligne.
	const groupes = useMemo(() => {
		if (!grouper) return null
		const map = new Map()
		liste.forEach((e) => {
			if (!map.has(e.vehicule.id))
				map.set(e.vehicule.id, { vehicule: e.vehicule, rows: [] })
			map.get(e.vehicule.id).rows.push(e)
		})
		return [...map.values()]
	}, [liste, grouper])

	const renderRow = (e) => (
		<EcheanceRow
			key={e.cycle_id}
			e={e}
			hovered={hovered === e.cycle_id}
			setHovered={setHovered}
			onOpen={onOpen}
			onCreerFiche={onCreerFiche}
			onVoirFiche={onVoirFiche}
			menuItems={menuItems}
		/>
	)

	return (
		<>
			<div style={styles.bar}>
				<input
					type="search"
					style={styles.search}
					placeholder="Rechercher un véhicule, un entretien…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
				/>
				<select
					style={styles.select}
					value={vehiculeId}
					onChange={(e) => setVehiculeId(e.target.value)}
				>
					<option value="">Tous les véhicules</option>
					{vehicules.map((v) => (
						<option key={v.id} value={v.id}>
							{v.label}
						</option>
					))}
				</select>
				{TYPES.map(([key, label]) => (
					<button
						key={key}
						type="button"
						style={gs.sectionTab(type === key)}
						onClick={() => setType(key)}
					>
						{label}
					</button>
				))}
				<button
					type="button"
					style={gs.sectionTab(grouper)}
					onClick={() => setGrouper((g) => !g)}
				>
					Grouper par véhicule
				</button>
			</div>
			<div style={ts.card}>
				<div style={ts.scroll}>
					<table style={ts.table}>
						<thead>
							<tr style={ts.tr}>
								<th
									style={{ ...ts.th, width: 4, padding: 0 }}
								/>
								<th style={ts.th}>Statut</th>
								<th style={ts.th}>Véhicule</th>
								<th style={ts.th}>Entretien</th>
								<th style={ts.th}>Cycle</th>
								<th style={ts.th}>Dernier</th>
								<th style={ts.th}>Prochaine échéance</th>
								<th style={ts.th}>Fiche atelier</th>
								<th style={ts.th} />
							</tr>
						</thead>
						<tbody>
							{liste.length === 0 ? (
								<tr style={ts.tr}>
									<td colSpan={NB_COLS} style={ts.empty}>
										{echeances.length === 0
											? "Aucune échéance ouverte."
											: "Aucune échéance ne correspond à ces filtres."}
									</td>
								</tr>
							) : groupes ? (
								groupes.map((g) => (
									<React.Fragment key={`g_${g.vehicule.id}`}>
										<GroupeVehicule vehicule={g.vehicule} />
										{g.rows.map(renderRow)}
									</React.Fragment>
								))
							) : (
								liste.map(renderRow)
							)}
						</tbody>
					</table>
				</div>
			</div>
		</>
	)
}

export default EntretienEcheances
